import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../../lib/api';
import { dateLabel } from '../../lib/format';
import { Pill } from '../../components/ui';

// 生产业务收口 #3：工件溯源
// 一级对象 physical_asset，维修周期 V1/V2/V3 为同一工件下的分段履历
// 时间线：收件 → 工单 → 工序 → 领退料 → 检验 → 不合格/返工/复检 → 交货/退件
// 只读，不提供状态写入口

type TraceEvent = { type: string; date?: string; label: string; detail?: string };
type CycleTrace = {
  version: number; scenario?: string; status?: string;
  intake_date?: string; returned_date?: string;
  findings?: string; recommendation?: string;
  events: TraceEvent[];
};
type AssetTraceData = {
  asset: { id: string; customer_serial?: string; asset_type?: string; customer_name?: string; customer_code?: string };
  cycles: CycleTrace[];
};

const EVENT_ICON: Record<string, string> = {
  intake: '📥', work_order: '📋', operation: '🔧', movement: '📦',
  inspection: '🔬', rework: '🔁', return: '↩️', delivery: '🚚',
};

export function AssetTraceabilityPage() {
  const [sp] = useSearchParams();
  const [assets, setAssets] = useState<any[]>([]);
  const [selected, setSelected] = useState<string | null>(sp.get('asset') || null);
  const [trace, setTrace] = useState<AssetTraceData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get<{ data: any[] }>('/api/physical-assets?limit=100');
        setAssets(res.data || []);
      } catch { /* */ }
      setLoading(false);
    })();
  }, []);

  useEffect(() => {
    if (!selected) { setTrace(null); return; }
    (async () => {
      try {
        const res = await api.get<{ data: AssetTraceData }>(`/api/physical-assets/${selected}/trace`);
        setTrace(res.data);
      } catch { setTrace(null); }
    })();
  }, [selected]);

  return (
    <div style={{ padding: 16 }}>
      <h2 style={{ margin: '0 0 4px' }}>工件溯源</h2>
      <p style={{ color: '#64748b', fontSize: 13, margin: '0 0 16px' }}>
        同一工件的完整履历：收件 → 工单 → 工序 → 领退料 → 检验 → 返工/复检 → 交货/退件。
        工件身份长期不变，维修周期 V1/V2/V3 为分段履历。本页只读。
      </p>

      {loading ? <div>加载中…</div> : !assets.length ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>
          暂无工件。通过「收件登记」建立工件后，此处显示溯源。
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: 16 }}>
          {/* 工件列表 */}
          <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
            <div style={{ padding: '10px 12px', background: '#f8fafc', fontWeight: 600, fontSize: 13 }}>工件</div>
            {assets.map((a) => (
              <div
                key={a.id}
                onClick={() => setSelected(a.id)}
                style={{
                  padding: '10px 12px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9',
                  background: selected === a.id ? '#e0f2fe' : 'transparent',
                }}
              >
                <div style={{ fontWeight: 600, fontSize: 13 }}>{a.customer_serial || '—'}</div>
                <div style={{ color: '#64748b', fontSize: 12 }}>{a.customer_name || ''} · {a.asset_type || ''}</div>
              </div>
            ))}
          </div>

          {/* 溯源时间线 */}
          <div>
            {!trace ? (
              <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>选择工件查看溯源</div>
            ) : (
              <>
                <div style={{ padding: 14, background: '#f8fafc', borderRadius: 8, marginBottom: 14 }}>
                  <div style={{ fontWeight: 700, fontSize: 16 }}>{trace.asset.customer_serial || '—'}</div>
                  <div style={{ color: '#64748b', fontSize: 13 }}>
                    {trace.asset.customer_name || ''} · {trace.asset.asset_type || '—'} · {trace.cycles.length} 个维修周期
                  </div>
                </div>

                {trace.cycles.map((cycle) => (
                  <div key={cycle.version} style={{ marginBottom: 20 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                      <Pill tone="info" label={`V${cycle.version}`} />
                      <span style={{ fontWeight: 600, fontSize: 14 }}>{cycle.findings || '维修'}</span>
                      <Pill tone={cycle.status === 'delivered' ? 'ok' : 'warn'} label={cycle.status || '—'} />
                    </div>
                    {cycle.recommendation && (
                      <div style={{ color: '#64748b', fontSize: 12, marginBottom: 8 }}>建议：{cycle.recommendation}</div>
                    )}

                    {/* 时间线 */}
                    <div style={{ borderLeft: '2px solid #e2e8f0', paddingLeft: 16 }}>
                      {cycle.events.map((ev, i) => (
                        <div key={i} style={{ marginBottom: 12, position: 'relative' }}>
                          <div style={{
                            position: 'absolute', left: -22, top: 4, width: 10, height: 10,
                            borderRadius: '50%', background: '#0d3b66',
                          }} />
                          <div style={{ fontSize: 13, fontWeight: 600 }}>
                            {EVENT_ICON[ev.type] || '•'} {ev.label}
                          </div>
                          <div style={{ fontSize: 12, color: '#64748b' }}>
                            {ev.date ? dateLabel(ev.date) : ''} {ev.detail ? `· ${ev.detail}` : ''}
                          </div>
                        </div>
                      ))}
                      {!cycle.events.length && (
                        <div style={{ color: '#94a3b8', fontSize: 13 }}>暂无事件</div>
                      )}
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
      )}

      <div style={{ marginTop: 20, padding: 12, background: '#fff3cd', borderRadius: 8, fontSize: 13 }}>
        <strong>本页只读。</strong>状态变化只能来自正式业务动作（收件登记 / 确认方案 / 完成工序 / 填写检验 / 完成维修 / 交货 / 退料）。
        工序、材料等尚未建立的真实数据允许为空，待生产人员确认后补齐。
      </div>
    </div>
  );
}
